/**
 * Static public pages — /about, /contact, /privacy, /terms, /cookies, /careers.
 * Shared dark Jewel & Luxury theme; uses simple PublicShell with sticky nav
 * + footer matching Landing.jsx aesthetic.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { useEffect } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lead-discovery-hub-3/artifacts/wg4n305a_AgenteLead%20Pro%20Logo.png";

const BG = "#0F172A";
const SURFACE = "#1E293B";
const GOLD = "#84CC16";
const TEXT_MUTED = "#A1A1AA";

export function PublicShell({ title, subtitle, children, testid }) {
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "dark");
    return () => { if (prev !== null) root.setAttribute("data-theme", prev); else root.removeAttribute("data-theme"); };
  }, []);

  return (
    <div data-testid={testid || "public-page"}
      className="min-h-screen antialiased font-body"
      style={{ background: BG, color: "#FFFFFF" }}>
      {/* Sticky glass nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ backgroundColor: "rgba(15,8,32,0.7)", borderColor: "rgba(132,204,22,0.2)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-brand">
            <img src={LOGO_URL} alt="AgenteLead Pro" className="h-10 w-10 object-contain" />
            <span className="font-display text-[19px] font-extrabold tracking-tight leading-none text-white">
              AgenteLead<span style={{ color: GOLD }}>.Pro</span>
            </span>
          </Link>
          <Link to="/signup"
            className="inline-flex items-center gap-1.5 text-[13px] font-bold px-4 py-2 rounded-md transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: GOLD, color: "#0F172A", boxShadow: "0 8px 30px -10px rgba(132,204,22,0.55)" }}
            data-testid="public-cta">
            Start free trial<ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Page header */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 lg:px-10">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>
            AgenteLead Pro
          </div>
          <h1 className="font-display mt-3 text-4xl md:text-5xl font-black tracking-tight" style={{ color: "#FFFFFF" }}>
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-[16px] leading-relaxed max-w-2xl" style={{ color: TEXT_MUTED }}>
              {subtitle}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <main className="pb-24">
        <div className="max-w-4xl mx-auto px-5 lg:px-10">
          <article className="prose prose-invert max-w-none">
            <div className="rounded-2xl border p-8 lg:p-10 space-y-6"
              style={{ backgroundColor: SURFACE, borderColor: "rgba(132,204,22,0.15)" }}>
              {children}
            </div>
          </article>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-10" style={{ borderColor: "rgba(132,204,22,0.15)", backgroundColor: "#0A0518" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 flex flex-wrap items-center justify-between gap-4 text-[12px]" style={{ color: TEXT_MUTED }}>
          <div>© 2026 AgenteLead Pro. All rights reserved.</div>
          <nav className="flex flex-wrap gap-5">
            <Link to="/about"   className="hover:text-white" data-testid="footer-link-about">About</Link>
            <Link to="/contact" className="hover:text-white" data-testid="footer-link-contact">Contact</Link>
            <Link to="/privacy" className="hover:text-white" data-testid="footer-link-privacy">Privacy</Link>
            <Link to="/terms"   className="hover:text-white" data-testid="footer-link-terms">Terms</Link>
            <Link to="/cookies" className="hover:text-white" data-testid="footer-link-cookies">Cookies</Link>
            <Link to="/careers" className="hover:text-white" data-testid="footer-link-careers">Careers</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-display text-2xl font-bold mt-6" style={{ color: GOLD }}>{children}</h2>;
const P = ({ children }) => <p className="text-[15px] leading-relaxed" style={{ color: TEXT_MUTED }}>{children}</p>;

export function AboutPage() {
  return (
    <PublicShell testid="page-about" title="About AgenteLead Pro"
      subtitle="The sales cockpit for B2B local prospecting. Built by operators who lived in spreadsheets — and refused to keep doing it.">
      <H>Our mission</H>
      <P>We help agencies, consultants and SDR teams turn local searches into predictable revenue. One cockpit, real data sources, AI-grade audits, drag-and-drop CRM and automated outreach — without the spreadsheet tax.</P>
      <H>What makes us different</H>
      <P>Most prospecting tools are stuck in 2018: scraped lists, no scoring, no audit, no follow-through. AgenteLead Pro pulls from official APIs (Google Maps, LinkedIn Sales Nav, YouTube Data) and bolts on GPT reasoning so every lead lands in your Kanban with context, score and a ready-to-send pitch.</P>
      <H>Team</H>
      <P>A small, distributed team obsessed with go-to-market velocity. Engineering in São Paulo + Lisbon. Customer Success in Miami.</P>
    </PublicShell>
  );
}

export function ContactPage() {
  return (
    <PublicShell testid="page-contact" title="Contact us"
      subtitle="Talk to a human. We respond within one business day.">
      <div className="grid md:grid-cols-3 gap-5">
        <a href="mailto:hello@agentelead-pro.com" className="rounded-xl p-5 border hover:border-[#84CC16]/60 transition-colors" style={{ borderColor: "rgba(132,204,22,0.2)" }} data-testid="contact-email">
          <Mail className="w-5 h-5 mb-3" style={{ color: GOLD }} />
          <div className="text-white font-bold text-[14px]">Email</div>
          <div className="text-[13px]" style={{ color: TEXT_MUTED }}>hello@agentelead-pro.com</div>
        </a>
        {(process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/\D/g, "") ? (
        <a href={`https://wa.me/${(process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="rounded-xl p-5 border hover:border-[#84CC16]/60 transition-colors" style={{ borderColor: "rgba(132,204,22,0.2)" }} data-testid="contact-whatsapp">
          <Phone className="w-5 h-5 mb-3" style={{ color: GOLD }} />
          <div className="text-white font-bold text-[14px]">WhatsApp</div>
          <div className="text-[13px]" style={{ color: TEXT_MUTED }}>Sales · Support</div>
        </a>
        ) : (
        <a href="mailto:hello@agentelead-pro.com?subject=Sales%20%2F%20Support" className="rounded-xl p-5 border hover:border-[#84CC16]/60 transition-colors" style={{ borderColor: "rgba(132,204,22,0.2)" }} data-testid="contact-whatsapp">
          <Phone className="w-5 h-5 mb-3" style={{ color: GOLD }} />
          <div className="text-white font-bold text-[14px]">Sales · Support</div>
          <div className="text-[13px]" style={{ color: TEXT_MUTED }}>hello@agentelead-pro.com</div>
        </a>
        )}
        <div className="rounded-xl p-5 border" style={{ borderColor: "rgba(132,204,22,0.2)" }} data-testid="contact-office">
          <MapPin className="w-5 h-5 mb-3" style={{ color: GOLD }} />
          <div className="text-white font-bold text-[14px]">Offices</div>
          <div className="text-[13px]" style={{ color: TEXT_MUTED }}>Miami · São Paulo · Lisbon</div>
        </div>
      </div>
      <H>Sales</H>
      <P>Looking for a custom plan or enterprise rollout? Email <span className="text-white">sales@agentelead-pro.com</span>.</P>
      <H>Support</H>
      <P>Existing customers can reach us 24/7 via in-app chat or <span className="text-white">support@agentelead-pro.com</span>.</P>
    </PublicShell>
  );
}

export function PrivacyPage() {
  return (
    <PublicShell testid="page-privacy" title="Privacy Policy"
      subtitle="Last updated: February 2026. We collect the minimum data required to run the product, never sell it, and let you delete it anytime.">
      <H>1. Data we collect</H>
      <P>Account data (email, name, company), usage telemetry (pages visited, feature events), and lead data you import or generate inside the platform.</P>
      <H>2. How we use it</H>
      <P>To provide the service, send service-related emails, debug issues, and improve the product. We never share or sell your data.</P>
      <H>3. Sub-processors</H>
      <P>We use industry-standard sub-processors (MongoDB Atlas, Stripe, OpenAI, Apify) under signed DPAs. Lead data is encrypted at rest and in transit.</P>
      <H>4. Your rights (GDPR/LGPD)</H>
      <P>Access, correction, deletion and portability — email <span className="text-white">privacy@agentelead-pro.com</span> and we respond within 30 days.</P>
    </PublicShell>
  );
}

export function TermsPage() {
  return (
    <PublicShell testid="page-terms" title="Terms of Service"
      subtitle="Last updated: February 2026. By using AgenteLead Pro you agree to these terms.">
      <H>1. Service</H>
      <P>AgenteLead Pro is a B2B SaaS for local prospecting. We grant you a non-exclusive, non-transferable right to use the platform under your active subscription.</P>
      <H>2. Acceptable Use</H>
      <P>No spam, no scraping of third-party sites that violate their ToS, no resale of raw data. Outreach must comply with CAN-SPAM, GDPR and your local laws.</P>
      <H>3. Subscription &amp; trial</H>
      <P>14-day free trial, no credit card required. Paid plans renew monthly. Cancel anytime — you keep access until the end of the billing period.</P>
      <H>4. Liability</H>
      <P>Service provided "as is". We are not liable for indirect or consequential damages. Total liability capped at the last 12 months of fees paid.</P>
    </PublicShell>
  );
}

export function CookiesPage() {
  return (
    <PublicShell testid="page-cookies" title="Cookie Policy"
      subtitle="We use a small set of cookies — and we tell you exactly what each one does.">
      <H>Strictly necessary</H>
      <P>Auth session (`alp.access_token`, `session_token`), CSRF protection, language preference. Cannot be disabled — the app will not work without them.</P>
      <H>Analytics</H>
      <P>Anonymous usage events (page views, feature clicks) to improve the product. Aggregated, no PII.</P>
      <H>Opt-out</H>
      <P>You can reject non-essential cookies in your browser at any time without losing access to the app.</P>
    </PublicShell>
  );
}

export function CareersPage() {
  return (
    <PublicShell testid="page-careers" title="Careers"
      subtitle="We're a small, ambitious team. Remote-first across LATAM, EU and US. If you ship fast and care about craft — talk to us.">
      <H>Open roles</H>
      <P>· Senior Full-Stack Engineer (React + FastAPI) — remote</P>
      <P>· GTM Engineer / Sales Ops — remote</P>
      <P>· Customer Success Lead — Miami / Remote</P>
      <H>How we hire</H>
      <P>Async-first interviews. One technical conversation, one paid take-home, one founders' chat. Decisions in &lt; 14 days.</P>
      <H>Apply</H>
      <P>Send your resume + a 2-paragraph "what would you ship in your first 30 days" to <span className="text-white">careers@agentelead-pro.com</span>.</P>
    </PublicShell>
  );
}
